import {
    Capability,
    type ClientRegistryAuthorizationRequest,
    type ClientRegistryData,
    type ClientRegistryRequest,
    ConnectwareError,
    ConnectwareErrorType,
    Translation,
} from '../../../domain'

import type { ClientRegistryService, TranslationService } from '../../../application'

import type { BackendJsonResponseContent } from '../../Connectware'
import { type ConnectwareHTTPServiceOptions, FetchConnectwareHTTPService } from '../Base'
import {
    mapAuthenticationMethodToGrantType,
    mapCybusUserAuthenticationMethodToAuthenticationMethod,
    mapEditableCybusPermissions,
    mapPermissionPostRequest,
    mapToDefault,
} from './mappers'

export class ConnectwareHTTPClientRegistryService extends FetchConnectwareHTTPService implements ClientRegistryService {
    constructor (options: ConnectwareHTTPServiceOptions, private readonly translationService: TranslationService) {
        super(options)
    }

    async lock (): Promise<void> {
        await this.request<'/api/client-registry/lock', 'POST', void>({
            capability: Capability.CLIENT_REGISTRY_MANAGE,
            path: '/api/client-registry/lock',
            authenticate: true,
            method: 'POST',
            handlers: {
                204: () => Promise.resolve(),
                401: () => new ConnectwareError(ConnectwareErrorType.AUTHENTICATION, 'User not authenticated to lock the client registry'),
            },
        })
    }

    async unlock (): Promise<void> {
        await this.request<'/api/client-registry/open', 'POST', void>({
            capability: Capability.CLIENT_REGISTRY_MANAGE,
            path: '/api/client-registry/open',
            authenticate: true,
            method: 'POST',
            body: {},
            handlers: {
                204: () => Promise.resolve(),
                401: () => new ConnectwareError(ConnectwareErrorType.AUTHENTICATION, 'User not authenticated to open the client registry'),
            },
        })
    }

    async fetchData (): Promise<ClientRegistryData> {
        return Promise.all([
            this.request<'/api/client-registry/status', 'GET', BackendJsonResponseContent<'/api/client-registry/status', 'GET', 200>>({
                capability: Capability.CLIENT_REGISTRY_MANAGE,
                path: '/api/client-registry/status',
                authenticate: true,
                method: 'GET',
                handlers: { 200: (response) => response.getJson() },
            }),
            this.request<'/api/client-registry', 'GET', BackendJsonResponseContent<'/api/client-registry', 'GET', 200>>({
                capability: Capability.CLIENT_REGISTRY_MANAGE,
                path: '/api/client-registry',
                authenticate: true,
                method: 'GET',
                handlers: {
                    200: (response) => response.getJson(),
                    401: () => new ConnectwareError(ConnectwareErrorType.AUTHENTICATION, 'User not authenticated to fetch client registry data'),
                },
            }),
        ]).then(([{ unlockedUntil }, requests]) => ({
            unlockedUntil: unlockedUntil ? new Date(unlockedUntil) : null,
            requests: requests
                .filter((r) => !r.granted)
                .map<ClientRegistryRequest>(({ timestamp, username, credentialType, roles, context, permissions }) => ({
                    username,
                    timestamp: new Date(timestamp),
                    authenticationMethod: mapCybusUserAuthenticationMethodToAuthenticationMethod(credentialType),
                    roles,
                    description: context,
                    permissions: mapEditableCybusPermissions(permissions),
                })),
        }))
    }

    authorize (request: ClientRegistryAuthorizationRequest): Promise<void> {
        return this.request<'/api/client-registry/confirm', 'POST', void>({
            capability: Capability.CLIENT_REGISTRY_MANAGE,
            path: '/api/client-registry/confirm',
            authenticate: true,
            method: 'POST',
            body: {
                username: request.username,
                roles: request.roles.map(({ name }) => name),
                mqttPublishPrefix: mapToDefault(request.mqttPublishPrefix, ''),
                initialPermissions: request.permissions.map(mapPermissionPostRequest),
                grantTypes: request.authenticationMethods.map(mapAuthenticationMethodToGrantType),
            },
            handlers: {
                204: () => Promise.resolve(),
                400: () =>
                    new ConnectwareError(ConnectwareErrorType.BAD_REQUEST, 'Unexpected bad request detected while processing authorization request', request),
                401: () => new ConnectwareError(ConnectwareErrorType.AUTHENTICATION, 'User not authenticated to confirm client registry registration'),
                404: () => new ConnectwareError(ConnectwareErrorType.NOT_FOUND, 'Could not find user to register', { username: request.username }),
                409: () => new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'User already confirmed', { username: request.username }),
                500: async (response) => {
                    const json = await response.getJson()

                    switch (json.message) {
                        case 'Invalid CSR':
                            return new ConnectwareError(
                                ConnectwareErrorType.GENERAL_BUSINESS_RULE_INFRACTION,
                                this.translationService.translate(Translation.INVALID_CSR)
                            )
                        case 'CSR uses empty Common Name or the Common Name does not match the username from the payload.':
                            return new ConnectwareError(
                                ConnectwareErrorType.GENERAL_BUSINESS_RULE_INFRACTION,
                                this.translationService.translate(Translation.CSR_COMMON_NAME_ISSUE)
                            )
                        case undefined:
                            return new ConnectwareError(ConnectwareErrorType.SERVER_ERROR, '500 Internal Server Error', { data: json })
                        default:
                            return new ConnectwareError(
                                ConnectwareErrorType.GENERAL_BUSINESS_RULE_INFRACTION,
                                this.translationService.translate(Translation.CLIENT_REGISTRATION_FAILED)
                            )
                    }
                },
            },
        })
    }
}
